/** @jsx jsx */
import React from 'react';
import { jsx, css } from '@compiled/react';
import noop from 'lodash/noop';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, xcss, Inline } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { token } from '@atlaskit/tokens';
import type { IconProps } from '@atlaskit/icon';
import { connect } from '@atlassian/jira-issue-view-react-redux/src/index.tsx';
import type { State } from '@atlassian/jira-issue-view-common-types/src/issue-type.tsx';
import { ActionButton } from '@atlassian/jira-issue-view-common-types/src/comment-type.tsx';

import { loadingMoreChildCommentsSelector } from '@atlassian/jira-issue-view-store/src/selectors/comment-selector.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import ReplyCommentWrapper from '../comment-reply-wrapper/index.tsx';
import { messages } from './messages.tsx';

type commentId = string;

type ReplyButtonStateProp = {
	activeLoadingButton: keyof typeof ActionButton | null;
};

type ComponentProps = {
	type: keyof typeof ActionButton;
	commentId: commentId;
	onShowMoreClickHandler: (analyticsEvent: UIAnalyticsEvent) => void;
	onShowLessClickHandler?: (analyticsEvent: UIAnalyticsEvent) => void;
	forceShowLoadingState?: boolean;
	isReplyingToComment?: boolean;
	showLessButton?: boolean;
	showMoreButton?: boolean;
};

type CollapseRepliesContainerProps = ComponentProps & ReplyButtonStateProp;

const CollapseRepliesContainer = ({
	onShowMoreClickHandler,
	onShowLessClickHandler = noop,
	activeLoadingButton,
	type,
	forceShowLoadingState,
	isReplyingToComment,
	showLessButton,
	showMoreButton,
}: CollapseRepliesContainerProps) => {
	const { formatMessage } = useIntl();

	if (
		!showMoreButton &&
		(!showLessButton || !expVal('jira_collapse_threaded_comment', 'isEnabled', false))
	) {
		return null;
	}

	return (
		<ReplyCommentWrapper
			showNextButton={type === ActionButton.NEXT}
			isLast={!isReplyingToComment && type === ActionButton.NEXT}
		>
			<Inline
				alignBlock="center"
				xcss={containerStyles}
				testId="issue-view-activity-comment.comment-collapse-replies-container.container"
			>
				<div
					data-testId="issue-view-activity-comment.comment-collapse-replies-container.empty-container"
					css={[emptyContainerStyles, customCommentStyles]}
				/>
				{showMoreButton && (
					<Box xcss={buttonContainer}>
						<Button
							onClick={(
								e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
								analyticsEvent: UIAnalyticsEvent,
							) => onShowMoreClickHandler(analyticsEvent)}
							appearance="subtle"
							iconAfter={(iconProps: IconProps) => <ChevronDownIcon {...iconProps} size="small" />}
							isLoading={activeLoadingButton === type || forceShowLoadingState}
							testId="issue-view-activity-comment.comment-collapse-replies-container.show-more-button"
						>
							{formatMessage(messages.showMoreRepliesText)}
						</Button>
					</Box>
				)}
				{showLessButton && expVal('jira_collapse_threaded_comment', 'isEnabled', false) && (
					<Box xcss={buttonContainer}>
						<Button
							onClick={(
								e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
								analyticsEvent: UIAnalyticsEvent,
							) => onShowLessClickHandler(analyticsEvent)}
							appearance="subtle"
							iconAfter={(iconProps: IconProps) => <ChevronUpIcon {...iconProps} size="small" />}
							testId="issue-view-activity-comment.comment-collapse-replies-container.show-less-button"
							isDisabled={activeLoadingButton === type || forceShowLoadingState}
						>
							{formatMessage(messages.showLessRepliesText)}
						</Button>
					</Box>
				)}
			</Inline>
		</ReplyCommentWrapper>
	);
};

const containerStyles = xcss({
	position: 'relative',
});

const buttonContainer = xcss({
	position: 'relative',
	justifyContent: 'flex-end',
	gap: 'space.100',
});

const emptyContainerStyles = css({
	paddingTop: token('space.250'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.250'),
	paddingLeft: token('space.050'),
	// 32 px for the padding given to the parent container (24+8)
	maxWidth: 'calc(100% - 32px)',
	position: 'relative',
	'--commentLineWidth': '1px',
	'--commentLineColor': token('color.background.accent.gray.subtler'),
	// 8px padding + 0.5px (to center align line of 1px) + 18px center align avatar (36px size) = 26.5px

	'--childTailWidth': token('space.200'),
	'&::after': {
		boxSizing: 'border-box',
		content: '',
		position: 'absolute',
		insetInlineStart: 'var(--commentInlineStart)',
		top: 'calc(-50% - var(--commentLineWidth)/2)',
		width: 'var(--childTailWidth)',
		borderInlineStart: 'var(--commentLineWidth) solid var(--commentLineColor)',
		borderBottom: 'var(--commentLineWidth) solid var(--commentLineColor)',
		backgroundColor: token('color.background.neutral.subtle'),
		borderEndStartRadius: token('space.100'),
		height: '100%',
	},
});

const customCommentStyles = css({
	'&::after': {
		insetInlineStart: '-14px',
	},
});

export default connect((state: State, ownProps: ComponentProps) => ({
	activeLoadingButton: loadingMoreChildCommentsSelector(ownProps.commentId)(state),
}))(CollapseRepliesContainer);
